import { ArrowUpRight, Check } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { LEAD_MAGNETS } from "@/data/leadMagnets";
import { track } from "@/lib/analytics";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Contextual lead-magnet band — shows a preview of the diagnostic (sample
 * checks) before asking for details. Only rendered where a relevant resource
 * exists (SERVICE_LEAD_MAGNET map).
 */
export default function LeadMagnetSection({ testid, resource, onOpen }) {
  const magnet = LEAD_MAGNETS[resource];
  if (!magnet) return null;
  return (
    <section data-testid={testid} className="py-16 md:py-20 bg-[#050505] text-white">
      <div className={`${GRID} grid grid-cols-12 gap-10 items-center`}>
        <div className="col-span-12 md:col-span-6 reveal">
          <SectionBadge label="Free Diagnostic" tone="light" />
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-6 leading-[1.1] max-w-xl">
            {magnet.headline}
          </h2>
          <p className="mt-5 text-base text-white/70 leading-relaxed max-w-lg">{magnet.copy}</p>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-white/40">{magnet.scope}</p>
          <button
            data-testid={`${testid}-cta`}
            onClick={() => {
              track("lead_magnet_open", { resource, source_page: window.location.pathname });
              onOpen();
            }}
            className="group mt-8 inline-flex items-center gap-2 h-12 px-7 bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors duration-300"
          >
            {magnet.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        {/* Document preview — sample checks from the actual PDF */}
        <div className="col-span-12 md:col-span-6 reveal">
          <div className="bg-white text-black p-7 md:p-9 shadow-2xl md:rotate-1 transition-transform duration-500 hover:rotate-0">
            <div className="flex items-baseline justify-between gap-4 pb-4 border-b border-black/10">
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">
                {magnet.title}
              </div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-400">20 checks</div>
            </div>
            <ul className="mt-5 space-y-3.5">
              {magnet.preview.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-700">
                  <span className="mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center border border-black/30" style={{ width: 18, height: 18 }}>
                    <Check className="h-3 w-3" aria-hidden />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-black/10 text-xs text-zinc-500 italic">
              + sixteen more checks, scoring and interpretation inside
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
